import { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchErrorLog, clearErrorLog, type ErrorLogEntry } from '../api/errorLog';
import { uploadWp } from '../api/dev';
import { useWpMap, buildErrorKey } from '../hooks/useWpMap';
import { usePermission } from '../hooks/usePermission';
import { useSnackbar } from '../context/SnackbarContext';

function SourceChip({ source }: { source: ErrorLogEntry['source'] }) {
  return (
    <Chip
      label={source}
      size="small"
      color={source === 'backend' ? 'warning' : 'info'}
      variant="outlined"
    />
  );
}

function StatusChip({ status }: { status: number | null }) {
  if (!status) return null;
  const color = status >= 500 ? 'error' : status >= 400 ? 'warning' : 'default';
  return <Chip label={status} size="small" color={color} />;
}

function buildMarkdownBlob(row: ErrorLogEntry): File {
  const title = buildErrorKey(row);
  const today = new Date().toISOString().slice(0, 10);
  const md = [
    '---',
    `title: "${title}"`,
    `generated: ${today}`,
    '---',
    '',
    `# ${title}`,
    '',
    `**Source:** ${row.source}`,
    row.http_status != null ? `**HTTP Status:** ${row.http_status}` : null,
    row.method        != null ? `**Method:** ${row.method}`         : null,
    `**Path:** ${row.path}`,
    '',
    '## Message',
    '',
    row.message ?? '_no message_',
  ]
    .filter((l) => l !== null)
    .join('\n');

  const safeName = title.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 80);
  return new File([md], `${safeName}.md`, { type: 'text/markdown' });
}

const columns: GridColDef<ErrorLogEntry>[] = [
  {
    field: 'occurrences',
    headerName: 'Count',
    width: 80,
    renderCell: (p) => (
      <Typography variant="body2" fontWeight={700} color={p.value > 10 ? 'error.main' : 'text.primary'}>
        {p.value as number}
      </Typography>
    ),
  },
  {
    field: 'source',
    headerName: 'Source',
    width: 100,
    renderCell: (p) => <SourceChip source={p.value as ErrorLogEntry['source']} />,
  },
  {
    field: 'http_status',
    headerName: 'Status',
    width: 80,
    renderCell: (p) => <StatusChip status={p.value as number | null} />,
  },
  { field: 'method', headerName: 'Method', width: 80 },
  { field: 'path',   headerName: 'Path',   flex: 1, minWidth: 200 },
  { field: 'error_code', headerName: 'Code', width: 200 },
  {
    field: 'message',
    headerName: 'Message',
    flex: 1,
    minWidth: 200,
    renderCell: (p) => (
      <Tooltip title={String(p.value ?? '')} placement="top">
        <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
          {String(p.value ?? '')}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: 'last_seen',
    headerName: 'Last seen',
    width: 170,
    valueFormatter: (v: string) =>
      new Date(v).toLocaleString([], { dateStyle: 'short', timeStyle: 'medium' }),
  },
  {
    field: 'first_seen',
    headerName: 'First seen',
    width: 170,
    valueFormatter: (v: string) =>
      new Date(v).toLocaleString([], { dateStyle: 'short', timeStyle: 'medium' }),
  },
];

export function ErrorLog() {
  const canRead   = usePermission('audit.read');
  const canManage = usePermission('system.manage');
  const qc = useQueryClient();
  const showSnackbar = useSnackbar();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['error-log'],
    queryFn: fetchErrorLog,
    enabled: canRead,
    refetchInterval: 30_000,
  });

  type AxiosLike = { response?: { status?: number; data?: { error?: { message?: string } } }; message?: string };
  const axiosErr = isError ? (error as AxiosLike | null) : null;
  const errorStatus = axiosErr?.response?.status ?? 0;
  const errorMessage =
    axiosErr?.response?.data?.error?.message ??
    axiosErr?.message ??
    'Unexpected error';

  const rows = useMemo(
    () => (data?.errors ?? []).map((e, i) => ({ id: i, ...e })),
    [data],
  );

  const { map: wpMap, refresh: refreshWpMap } = useWpMap(rows);
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());

  const createWpMutation = useMutation({
    mutationFn: async (row: ErrorLogEntry & { id: number }) => {
      const file = buildMarkdownBlob(row);
      const form = new FormData();
      form.append('file', file);
      form.append('resolution', 'next');
      form.append('priority', '80');
      return uploadWp(form);
    },
    onMutate: (row) => {
      setPendingKeys((s) => new Set(s).add(buildErrorKey(row)));
    },
    onSettled: async (_data, _err, row) => {
      setPendingKeys((s) => { const n = new Set(s); n.delete(buildErrorKey(row)); return n; });
      await refreshWpMap();
      void qc.invalidateQueries({ queryKey: ['dev', 'wps'] });
    },
    onSuccess: (_data, row) => {
      showSnackbar(`Work package created for "${buildErrorKey(row)}"`, 'success');
    },
    onError: (_err, row) => {
      showSnackbar(`Failed to create WP for "${buildErrorKey(row)}"`, 'error');
    },
  });

  const actionColumn: GridColDef = {
    field: '_action',
    headerName: '',
    width: 56,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (p) => {
      const row = p.row as ErrorLogEntry & { id: number };
      const key = buildErrorKey(row);
      const wp = wpMap.get(key);
      const pending = pendingKeys.has(key);

      if (pending) {
        return <CircularProgress size={20} sx={{ my: 'auto' }} />;
      }
      if (!wp) {
        return (
          <Tooltip title="Create work package">
            <IconButton
              size="small"
              color="primary"
              onClick={() => createWpMutation.mutate(row)}
            >
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      }
      if (wp.status === 'done') {
        return (
          <Tooltip title="Resolved">
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20, my: 'auto' }} />
          </Tooltip>
        );
      }
      return (
        <Tooltip title="Work package in progress">
          <PauseIcon sx={{ color: 'warning.main', fontSize: 20, my: 'auto' }} />
        </Tooltip>
      );
    },
  };

  const allColumns = [actionColumn, ...columns];

  const handleClear = async () => {
    try {
      const { deleted } = await clearErrorLog();
      showSnackbar(`Cleared ${deleted} error record(s)`, 'success');
      void qc.invalidateQueries({ queryKey: ['error-log'] });
    } catch {
      showSnackbar('Failed to clear error log', 'error');
    }
  };

  if (!canRead) {
    return <Alert severity="warning">You do not have permission to view the error log.</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h5">Error Log</Typography>
          <Typography variant="body2" color="text.secondary">
            4xx/5xx errors captured from backend and frontend — sorted by frequency
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => void qc.invalidateQueries({ queryKey: ['error-log'] })}
          >
            Refresh
          </Button>
          {canManage && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteSweepIcon />}
              onClick={() => void handleClear()}
              disabled={rows.length === 0}
            >
              Clear all
            </Button>
          )}
        </Stack>
      </Stack>

      {isError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {errorStatus === 501
            ? 'Error log requires MySQL — add a mysql: block to app.yaml and restart the service.'
            : `Could not load error log — ${errorMessage}`}
        </Alert>
      )}

      <DataGrid
        rows={rows}
        columns={allColumns}
        loading={isLoading}
        autoHeight
        disableRowSelectionOnClick
        initialState={{
          sorting: { sortModel: [{ field: 'occurrences', sort: 'desc' }] },
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={{ bgcolor: 'background.paper' }}
      />
    </Box>
  );
}
